import { useMemo } from 'react';
import { Trans, useTranslation } from 'react-i18next';
import SectionDetails from '../../_Sections/SectionDetails';
import SectionGallery from '../../_Sections/SectionGallery';
import SectionDropship from '../../_Sections/SectionDropship';
import SectionPairings from '../../_Sections/SectionPairings';
import SectionBranding from '../../_Sections/SectionBranding';
import SectionSizes from '../../_Sections/SectionSizes';
import { useSelector } from 'react-redux';
import WebsiteStore from '../../../WebsiteStore';
import { Box, Container, Grid, Stack, Typography, styled } from '@mui/material';
import { Helmet } from 'react-helmet-async';
import SectionPricing from '../../_Sections/SectionPricing';
import tKey from '../../../Common/Utils/tKey';
import { useAppSelector } from '../../../Common/_hooks/useAppSelector';
import ProductSEO from '../../_Sections/ProductSEO';
import { BlackCheckedIcon } from '../../../Common/_components/CheckBoxes';
import SizeDisplay from '../../../Common/Measurements/SizeDisplay';

const PRODUCT_ID = 132;
const VIEW_PRODUCT_ID = 154;

const CustomWarning = styled('div')(({ theme }) => ({
  marginTop: 60,
  color: 'white',
  textAlign: 'center',
}))

export default function PlacematPage() {
  const [t] = useTranslation('page/products/placemats');
  const productData = useAppSelector(state => state.get('appData').get('products').get(String(PRODUCT_ID)));
  const locale = useAppSelector(state => state.get('userData').get('prefs').get('lang'))

  if (!productData) return null

  return <div className="centered-page product-page-v4">
    <ProductSEO
      viewProductId={VIEW_PRODUCT_ID}
      title={t('Custom all-over print Placemats | Products')}
      description={t('Design and create your own custom printed Placemats featuring your artwork. ✔ Print-on-demand drop shipping ✔ No minimums ✔ Made in Canada')}
    />
    <SectionDetails
      viewProductId={VIEW_PRODUCT_ID}
      color="#FFE4B5"
      bgStyle={{
        backgroundPosition: 'center right',
        backgroundRepeat: 'no-repeat',
        backgroundSize: '47%',
      }}
      fullPageBg
      tag={t('The all-over print')}
      title={t('Placemat')}
      text={<Trans
        t={t}
        i18nKey={tKey('Elevate your table setting with custom printed placemats that make every meal feel like an occasion. Made from two layers of durable linen-look fabric, these <size/> placemats have a polished, semi-rigid structure and feature vibrant, permanent sublimation prints. A crisp white back and double edge stitching complete the clean, modern finish.')}
        components={{
          size: <SizeDisplay sizes={[17.5, 13]} sourceUnit="in" displayType="physical" />,
        }}
      />}
      createUrl={productData.get('url_slug')}
      img={require('@resources/img/pages/products/placemats/banner-1-' + locale + '.png')}
      alt={t('Picture of Placemat')}
      details={[
        {
          img: require('@resources/img/pages/products/placemats/icon-1.svg?url'),
          alt: t('Placemat icon'),
          text: t('Double layer linen-look 100% polyester fabric'),
        },
        {
          img: require('@resources/img/pages/products/placemats/icon-2.svg?url'),
          alt: t('Heat and moisture icon'),
          text: t('Semi-rigid structure that’s easy to care for and store'),
        },
        {
          img: require('@resources/img/pages/products/placemats/icon-3.svg?url'),
        alt: t('Rubberized texture icon'),
          text: t('Double top stitch finishing'),
        },
        {
          img: require('@resources/img/pages/products/placemats/icon-4.svg?url'),
          alt: t('Print icon'),
          text: t('Durable and vibrant sublimation printed front and white back'),
        },
        {
          img: require('@resources/img/pages/products/placemats/icon-5.svg?url'),
          alt: t('Canada icon'),
          text: t('Digitally printed on demand in Montreal, Canada'),
        },
      ]}
    />
    <SectionGallery images={[
      require('@resources/img/pages/products/placemats/gallery_0.jpg'),
      require('@resources/img/pages/products/placemats/gallery_1.jpg'),
      require('@resources/img/pages/products/placemats/gallery_2.jpg'),
      require('@resources/img/pages/products/placemats/gallery_3.jpg'),
      require('@resources/img/pages/products/placemats/gallery_4.jpg'),
    ]} />

    <SectionDropship
      title={<Trans
        t={t}
        i18nKey={tKey('<yellow>Drop ship</yellow> your Placemat<br/> <pink>on-demand</pink>')}
        components={{
          yellow: <span className="ty" />,
          pink: <span className="tp" />,
        }}
      />}
      text={t('Your brand and following will love Placemat! At prices designed for reselling, Placemat made on demand meet the needs of sellers who prefer not to stock inventory. Sell them in your eCommerce store and decide your profit margin')}
    />

    <SectionPricing
      productId={PRODUCT_ID}
      listOptions
    />

    <SectionSizes
      productId={PRODUCT_ID}
      title={t('Placemat Sizing')}
      text={<Trans
        t={t}
        i18nKey={tKey('Available in 1 size: <size/>. Sold as individual pieces, or as a set of 4.')}
        components={{
          size: <SizeDisplay sizes={[17.5, 13]} sourceUnit="in" displayType="physical" />,
        }}
      />}
    >
      <img
        src={require('@resources/img/pages/products/placemats/measurement.svg?url')}
        style={{
          marginRight: 0,
        }}
      />
      <CustomWarning>
        <p>{t('When designing your placemats, we recommend avoiding borders around the outside of your design as they may not perfectly align on the finished product. Expect some variance between your design placement in the Design Lab versus the finished printed product.')}</p>
      </CustomWarning>
    </SectionSizes>

    <SectionBranding
      title={t('Brand your Placemat')}
      text={t('Branding enhances your products. Add custom labels, artist cards or stickers to your packages to create a cohesive message and build your brand.')}
    />

    <SectionPairings
      text={t('Pair your Placemat with these great products to create your own unique styles.')}
      productSlugs={['coaster', 'desk-mat', 'stainless-steel-tumbler']}
    />
  </div>
}
